"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SuperblocksEmbed = exports.ERROR_MESSAGE = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const ErrorImage_1 = require("./ErrorImage");
const types_1 = require("./types");
const utils_1 = require("./utils");
exports.ERROR_MESSAGE = "Whoops! There was an error loading the page. Please refresh to try again or contact your administrator for assistance.";
exports.SuperblocksEmbed = react_1.default.forwardRef((props, ref) => {
    const { id, properties, token, src, colorScheme, onAppLoaded, onAuthError, onNavigation, onEvent, style, } = props;
    const iframeRef = (0, react_1.useRef)(null);
    const [error, setError] = (0, react_1.useState)(null);
    const hasBeenAcknowledged = (0, react_1.useRef)(false);
    const targetOrigin = (0, react_1.useMemo)(() => {
        return (0, utils_1.extractTargetOrigin)(src);
    }, [src]);
    const iframeId = (0, react_1.useRef)(Date.now());
    (0, react_1.useEffect)(() => {
        try {
            (0, utils_1.validateAttributes)(props);
        }
        catch (e) {
            setError(`Invalid props: ${e.message}`);
            console.error(e.message);
        }
    }, [props]);
    const iframeSrc = (0, react_1.useMemo)(() => {
        if (!src)
            return "";
        try {
            const url = new URL(src);
            url.searchParams.set("embed_id", `${iframeId.current}`);
            return url.toString();
        }
        catch (e) {
            setError(`Invalid props: ${e.message}`);
            console.error(`Invalid props: ${e.message}`);
        }
    }, [src]);
    // Handle events from iframe
    const eventHandler = (0, react_1.useCallback)((event) => {
        if (event.origin !== targetOrigin)
            return;
        const typedEvent = event.data;
        const eventType = typedEvent.type;
        const data = typedEvent.data;
        switch (eventType) {
            case types_1.IncomingMessage.INITIALIZED:
                hasBeenAcknowledged.current = true;
                break;
            case types_1.IncomingMessage.APP_LOADED:
                typeof onAppLoaded === "function" &&
                    onAppLoaded(data);
                break;
            case types_1.IncomingMessage.NAVIGATION:
                typeof onNavigation === "function" &&
                    onNavigation(data);
                break;
            case types_1.IncomingMessage.AUTH_ERROR:
                typeof onAuthError === "function" &&
                    onAuthError(data);
                break;
            case types_1.IncomingMessage.EMIT_EVENT: {
                if (!data)
                    return;
                const typedData = data;
                typeof onEvent === "function" &&
                    onEvent(typedData.eventName, typedData.payload);
                break;
            }
            default:
                break;
        }
    }, [onAppLoaded, onAuthError, onNavigation, targetOrigin]);
    (0, react_1.useEffect)(() => {
        window.addEventListener("message", eventHandler);
        return () => {
            window.removeEventListener("message", eventHandler);
        };
    }, [eventHandler]);
    // initialie iframe
    const onIframeLoad = (0, react_1.useCallback)(() => {
        if (hasBeenAcknowledged.current)
            return;
        try {
            iframeRef.current?.contentWindow?.postMessage({
                type: types_1.OutgoingMessage.INITIALIZE_EMBED,
                data: {
                    token,
                    properties,
                    colorScheme,
                },
            }, "*");
            setError(null);
        }
        catch (e) {
            setError(`Failed to initialize the Superblocks Embed ${e?.message ?? ""}`);
            console.error(`Failed to initialize the Superblocks Embed ${e?.message ?? ""}`);
        }
    }, [token, properties]);
    const iframeLoadCalbackWithRetry = (0, react_1.useCallback)(() => {
        if (!hasBeenAcknowledged.current) {
            onIframeLoad();
            setTimeout(() => {
                iframeLoadCalbackWithRetry();
            }, 100);
        }
    }, [onIframeLoad]);
    // handle updates of properties, colorScheme and token
    (0, react_1.useEffect)(() => {
        if (iframeRef.current) {
            try {
                iframeRef.current.contentWindow?.postMessage({
                    type: types_1.OutgoingMessage.PROPERTY_CHANGE,
                    data: { properties },
                }, "*");
            }
            catch (e) {
                console.error(`Failed to update properties of the Superblocks Embed ${e?.message ?? ""}`);
            }
        }
    }, [properties]);
    (0, react_1.useEffect)(() => {
        if (iframeRef.current) {
            try {
                iframeRef.current.contentWindow?.postMessage({
                    type: types_1.OutgoingMessage.TOKEN_CHANGE,
                    data: { token },
                }, "*");
            }
            catch (e) {
                console.error(`Failed to update token of the Superblocks Embed ${e?.message ?? ""}`);
            }
        }
    }, [token]);
    (0, react_1.useEffect)(() => {
        if (iframeRef.current) {
            try {
                iframeRef.current.contentWindow?.postMessage({
                    type: types_1.OutgoingMessage.COLOR_SCHEME_CHANGE,
                    data: { colorScheme },
                }, "*");
            }
            catch (e) {
                console.error(`Failed to update colorScheme of the Superblocks Embed ${e?.message ?? ""}`);
            }
        }
    }, [colorScheme]);
    // set up exposed functions
    (0, react_1.useImperativeHandle)(ref, () => ({
        trigger: (eventName, payload) => {
            if (iframeRef.current) {
                try {
                    iframeRef.current.contentWindow?.postMessage({
                        type: types_1.OutgoingMessage.TRIGGER_EVENT,
                        data: { eventName, payload },
                    }, "*");
                }
                catch (e) {
                    console.error(`Failed to trigger event ${eventName} of the Superblocks Embed ${e?.message ?? ""}`);
                }
            }
            else {
                console.warn("SuperblocksEmbed is not in a state to trigger events.");
            }
        },
    }));
    return error ? (react_1.default.createElement("div", { style: {
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "16px",
            justifyContent: "center",
            fontFamily: "Arial, sans-serif",
            ...style,
        } },
        react_1.default.createElement(ErrorImage_1.ErrorImage, null),
        react_1.default.createElement("div", { style: {
                color: "#1F2633",
                fontSize: "28px",
                lineHeight: "32px",
                fontWeight: 700,
            } }, "Load failed"),
        react_1.default.createElement("div", { style: {
                fontSize: "16px",
                color: "#6C7689",
                lineHeight: "24px",
                fontWeight: 400,
            }, "data-testid": "superblocks-error" }, exports.ERROR_MESSAGE))) : (react_1.default.createElement("iframe", { style: { height: "100%", width: "100%", border: "none", ...style }, src: iframeSrc, id: id, ref: iframeRef, onLoad: iframeLoadCalbackWithRetry, "data-testid": "superblocks-iframe", title: "Superblocks Embed", allowFullScreen: true }));
});
